import { CampaignCardActions } from './CampaignCardActions';
import { formatCampaignTargetValue } from '@/dashboards/CampaignPlanner/helpers/formatCampaignTargetValue';
import { getCampaignFlightStatus } from '@/dashboards/CampaignPlanner/helpers/getCampaignFlightStatus';
import { getStatusColor } from '@/dashboards/CampaignPlanner/helpers/getStatusColor';
import { OPTIMISATION_BASE_STYLES } from '@/dashboards/CampaignPlanner/helpers/optimisationBaseStyles';
import { useCampaignPlannerContext } from '@/dashboards/CampaignPlanner/hooks/useCampaignPlannerContext';
import { useCampaignPlannerUrl } from '@/dashboards/CampaignPlanner/hooks/useCampaignPlannerUrl';
import { Campaign } from '@/dashboards/CampaignPlanner/types';
import { DateFormat, formatDate } from '@/utils/dateUtils';
import { dashboardRootRoute, toPath } from '@/utils/routes';
import { Button, Card, Typography } from '@analytical-alley/ui';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
    HiArrowRight,
    HiCalendar,
    HiChartPie,
    HiCurrencyDollar,
    HiSparkles,
} from 'react-icons/hi';
import { useDashboardGlobalContext } from '@/dashboards/providers/dashboardGlobalContext';

interface Props {
    campaign: Campaign;
}

export const CampaignCard = ({ campaign }: Props) => {
    const {
        params: { projectId },
    } = useCampaignPlannerUrl();
    const { dashboardId } = useSearch({ strict: false });
    const navigate = useNavigate();

    const {
        setRequestCampaign,
        setDeleteCandidate,
        setSaveAsCampaignReference,
        setShowSaveCampaignDialog,
    } = useCampaignPlannerContext();
    const { contributionFormatter, isFinancialKPI } =
        useDashboardGlobalContext();

    const navigateToCampaign = async () => {
        await navigate({
            to: toPath(dashboardRootRoute, '/campaign-optimization/$campaignId'),
            params: { projectId, campaignId: campaign.id },
            search: {
                dashboardId,
                optimizationBase: campaign.optimizationBase,
                budget: campaign.budget,
                startDate: formatDate(campaign.startDate, DateFormat.URL),
                endDate: formatDate(campaign.endDate, DateFormat.URL),
            },
        });
    };

    const flightStatus = getCampaignFlightStatus(campaign);
    const isDraft = campaign.status === 'draft';
    const inReview = campaign.status === 'in_review';
    const isApproved = campaign.status === 'approved';

    const optimisationBaseStyle =
        OPTIMISATION_BASE_STYLES[campaign.optimizationBase];

    const targetValue = formatCampaignTargetValue(
        campaign.optimizationBase,
        campaign.budget,
        contributionFormatter,
    );

    return (
        <Card
            className={`relative z-0 overflow-visible flex flex-col w-full transition-all duration-300 hover:shadow-xl hover:scale-[1.02] group border-2 ${optimisationBaseStyle.border} hover:z-10 focus-within:z-20`}
        >
            <span
                className={`absolute -top-2.5 right-4 px-2 py-0.5 text-xs font-semibold rounded-full ${optimisationBaseStyle.tag}`}
            >
                {optimisationBaseStyle.label}
            </span>
            {/* Campaign Name with Icon */}
            <div className="flex flex-wrap items-start sm:items-center gap-3 mb-2">
                {/* Icon + Name */}
                <div className="flex items-start gap-2 flex-1 min-w-0">
                    <div className="shrink-0 mt-1">
                        <div
                            className={`md:w-10 md:h-10 sm:w-6 sm:h-6 rounded-lg bg-linear-to-br ${optimisationBaseStyle.icon} flex items-center justify-center`}
                        >
                            <HiSparkles className="w-5 h-5 text-white" />
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <Typography
                            variant="bodyM"
                            title={campaign.name}
                            className="justify-center align-middle font-semibold text-gray-900 group-hover:text-green-300 transition-colors truncate"
                        >
                            {campaign.name}
                        </Typography>
                    </div>
                </div>

                {/* Actions (button / badge) */}
                <div className="flex items-center gap-2 ml-auto basis-full sm:basis-auto justify-end">
                    <span
                        className={`text-xs font-semibold px-3 py-1 rounded-full border ${flightStatus.color}`}
                    >
                        {flightStatus.label}
                    </span>

                    <CampaignCardActions
                        canRequestReview={isDraft}
                        canDelete={isDraft}
                        onSaveAs={() => {
                            setSaveAsCampaignReference(campaign);
                            setShowSaveCampaignDialog(true);
                        }}
                        onRequestReview={() => setRequestCampaign(campaign)}
                        onDelete={() => setDeleteCandidate(campaign.id)}
                    />
                </div>
            </div>
            {/* Campaign Details */}
            <div className="space-y-3 mb-5">
                {/* Budget */}
                <div className="flex items-center gap-3 text-gray-700">
                    {(campaign.optimizationBase === 'budget' ||
                        isFinancialKPI) ? (
                        <HiCurrencyDollar className="w-5 h-5 text-green-600 shrink-0" />
                    ) : (
                        <HiChartPie className="w-5 h-5 text-blue-600 shrink-0" />
                    )}
                    <div className="flex-1">
                        <Typography variant="bodyXS" className="text-gray-500">
                            Target
                        </Typography>
                        <Typography variant="bodyS" className="font-semibold">
                            {targetValue}
                        </Typography>
                    </div>
                </div>

                {/* Period */}
                <div className="flex items-center gap-3 text-gray-700">
                    <HiCalendar className="w-5 h-5 text-blue-600 shrink-0" />
                    <div className="flex-1">
                        <Typography variant="bodyXS" className="text-gray-500">
                            Campaign Period
                        </Typography>
                        <Typography variant="bodyS" className="font-medium">
                            {formatDate(campaign.startDate, DateFormat.Normal)}{' '}
                            - {formatDate(campaign.endDate, DateFormat.Normal)}
                        </Typography>
                    </div>
                </div>

                {/* Review Status (button / badge) */}
                <div className="flex ">
                    {isDraft && (
                        <Button
                            size="xs"
                            color="light"
                            className="cursor-pointer"
                            onClick={() => setRequestCampaign(campaign)}
                        >
                            Request Review
                        </Button>
                    )}
                    {inReview && (
                        <span
                            className={`text-xs font-semibold px-2 py-1 rounded-full border ${getStatusColor(
                                campaign.status,
                            )}`}
                        >
                            In Review
                        </span>
                    )}
                    {isApproved && (
                        <span
                            className={`text-xs font-semibold px-2 py-1 rounded-full border ${getStatusColor(
                                campaign.status,
                            )}`}
                        >
                            Approved
                        </span>
                    )}
                </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200 mb-4"></div>

            {/* CTA Section */}
            <div className="flex flex-col gap-2 justify-start">
                <Button
                    size="sm"
                    color="blue"
                    className="w-full group-hover:shadow-md transition-all cursor-pointer"
                    onClick={navigateToCampaign}
                >
                    <span className="flex-1 text-left">
                        {!isDraft ? 'View Campaign' : 'Start Planning'}
                    </span>
                    <HiArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
                {isDraft && (
                    <Typography
                        variant="bodyXS"
                        className="text-center text-gray-500 mt-2"
                    >
                        Click to optimize and manage this campaign
                    </Typography>
                )}
            </div>
        </Card>
    );
};
